import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Ear, Package, CheckCircle, Clock, ArrowClockwise,
  XCircle, Eye, Warning, Wrench, ClockCounterClockwise
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_FLOW = [
  { key: 'pending', label: 'Reçue', icon: Clock },
  { key: 'design', label: 'Design', icon: ArrowClockwise },
  { key: 'in_progress', label: 'Fabrication', icon: ArrowClockwise },
  { key: 'completed', label: 'Terminée', icon: CheckCircle },
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function materialLabel(m) {
  if (!m) return '—';
  const ml = m.toLowerCase();
  if (ml.includes('resine') || ml.includes('résine')) return 'Résine 3D';
  if (ml === 'silicone') return 'Silicone';
  return m;
}

function earLabel(s) {
  if (s === 'left') return 'Gauche (OG)';
  if (s === 'right') return 'Droit (OD)';
  if (s === 'both') return 'Bilatéral';
  return s || '—';
}

export default function OrderTracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stlLoading, setStlLoading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/track/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Lien invalide ou expiré');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const viewImpressionStl = async (fileId) => {
    setStlLoading(fileId);
    try {
      const res = await axios.post(`${API}/api/track/${token}/stl/${fileId}/viewer-link`);
      const viewerToken = res.data?.token;
      if (viewerToken) {
        window.open(`/viewer/${viewerToken}`, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Lien de visualisation indisponible');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'ouverture du visualiseur");
    } finally { setStlLoading(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
        <div className="bg-white border border-zinc-200 p-8 max-w-md text-center" data-testid="tracking-error">
          <Warning size={48} className="mx-auto mb-4 text-amber-500" weight="duotone" />
          <h2 className="font-heading font-bold text-lg mb-2">Lien indisponible</h2>
          <p className="text-sm text-zinc-600">{error}</p>
        </div>
      </div>
    );
  }

  const isCancelled = data.status === 'cancelled';
  const currentIdx = STATUS_FLOW.findIndex(s => s.key === data.status);

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="order-tracking-page">
      {/* Header */}
      <header className="bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ear size={24} weight="duotone" className="text-white" />
            <span className="font-heading font-bold text-sm tracking-widest">HAMAC SOLUTIONS</span>
          </div>
          <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Suivi de commande</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-5">
        {/* Hero */}
        <div className="bg-white border border-zinc-200 p-5 sm:p-7 rounded-sm">
          <p className="label-overline">Commande</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">
            {data.patient_reference}
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            {data.product_name} — {earLabel(data.ear_side)}
            {data.client_company ? <> · <span className="text-zinc-700 font-medium">{data.client_company}</span></> : null}
          </p>
        </div>

        {/* Status timeline */}
        {!isCancelled ? (
          <div className="bg-white border border-zinc-200 p-5 sm:p-7 rounded-sm" data-testid="status-timeline">
            <p className="label-overline mb-4">Avancement</p>
            <div className="flex items-center justify-between gap-2 relative">
              {STATUS_FLOW.map((s, i) => {
                const Icon = s.icon;
                const reached = i <= currentIdx;
                const isCurrent = i === currentIdx;
                return (
                  <div key={s.key} className="flex-1 flex flex-col items-center text-center relative z-10">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all ${
                      reached ? 'bg-zinc-900 border-zinc-900 text-white' : 'bg-white border-zinc-300 text-zinc-400'
                    } ${isCurrent ? 'ring-4 ring-zinc-900/10' : ''}`}>
                      <Icon size={20} weight={reached ? 'fill' : 'regular'} />
                    </div>
                    <p className={`mt-2 text-xs font-medium ${reached ? 'text-zinc-900' : 'text-zinc-400'}`}>
                      {s.label}
                    </p>
                  </div>
                );
              })}
              <div className="absolute top-[22px] left-[5%] right-[5%] h-0.5 bg-zinc-200 -z-0">
                <div
                  className="h-full bg-zinc-900 transition-all"
                  style={{ width: currentIdx >= 0 ? `${(currentIdx / (STATUS_FLOW.length - 1)) * 100}%` : '0%' }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-rose-50 border border-rose-200 p-5 rounded-sm flex items-center gap-3" data-testid="status-cancelled">
            <XCircle size={22} className="text-rose-600 flex-shrink-0" weight="fill" />
            <div>
              <p className="font-medium text-rose-900 text-sm">Commande annulée</p>
              <p className="text-xs text-rose-700">Cette commande n'est plus active.</p>
            </div>
          </div>
        )}

        {/* SAV banner */}
        {data.is_sav_refabrication && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm flex items-center gap-3">
            <Wrench size={20} className="text-amber-600 flex-shrink-0" weight="duotone" />
            <div>
              <p className="font-medium text-amber-900 text-sm">Refabrication SAV</p>
              <p className="text-xs text-amber-700">SAV {data.sav_number || ''} — suivi dédié après connexion.</p>
            </div>
          </div>
        )}

        {/* Characteristics */}
        <div className="bg-white border border-zinc-200 p-5 sm:p-7 rounded-sm" data-testid="characteristics">
          <p className="label-overline mb-4">Caractéristiques</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div><span className="text-zinc-400 text-xs">Réf. patient</span><p className="font-medium">{data.patient_reference}</p></div>
            <div><span className="text-zinc-400 text-xs">Côté</span><p className="font-medium">{earLabel(data.ear_side)}</p></div>
            <div><span className="text-zinc-400 text-xs">Produit</span><p className="font-medium">{data.product_name || '—'}</p></div>
            <div><span className="text-zinc-400 text-xs">Matière</span><p className="font-medium">{materialLabel(data.material)}</p></div>
            <div><span className="text-zinc-400 text-xs">Couleur</span><p className="font-medium capitalize">{data.color || '—'}</p></div>
            {data.serial_number_left && <div><span className="text-zinc-400 text-xs">N° série OG</span><p className="font-mono text-xs">{data.serial_number_left}</p></div>}
            {data.serial_number_right && <div><span className="text-zinc-400 text-xs">N° série OD</span><p className="font-mono text-xs">{data.serial_number_right}</p></div>}
            {data.delivery_date && <div><span className="text-zinc-400 text-xs">Livraison souhaitée</span><p className="font-medium">{formatDate(data.delivery_date)}</p></div>}
            <div><span className="text-zinc-400 text-xs">Créée le</span><p className="font-medium">{formatDate(data.created_at)}</p></div>
          </div>

          {data.selected_options?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Options</p>
              <p className="text-sm">{data.selected_options.map(o => o.name).join(', ')}</p>
            </div>
          )}

          {data.notes && (
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Notes</p>
              <p className="text-sm bg-zinc-50 p-3 rounded-sm whitespace-pre-wrap">{data.notes}</p>
            </div>
          )}
        </div>

        {/* Impression STLs */}
        {data.stl_files?.length > 0 && (
          <div className="bg-white border border-zinc-200 p-5 sm:p-7 rounded-sm" data-testid="tracking-stls">
            <div className="flex items-center gap-2 mb-4">
              <Package size={18} className="text-zinc-700" />
              <p className="label-overline m-0">Empreintes scannées ({data.stl_files.length})</p>
            </div>
            <div className="space-y-2">
              {[...data.stl_files].sort((a, b) => {
                if (a.side !== b.side) return a.side === 'left' ? -1 : 1;
                return (b.version || 0) - (a.version || 0);
              }).map((f) => (
                <div key={f.file_id} className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium truncate">{f.filename}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700">V{f.version || 1}</span>
                      <span className="text-[10px] text-zinc-500">{f.side === 'left' ? 'OG' : 'OD'}</span>
                    </div>
                    {f.uploaded_at && (
                      <span className="text-[10px] text-zinc-400">Déposé le {formatDateTime(f.uploaded_at)}</span>
                    )}
                  </div>
                  <Button size="sm" onClick={() => viewImpressionStl(f.file_id)}
                    disabled={stlLoading === f.file_id}
                    className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-8 text-xs"
                    data-testid={`track-view-stl-${f.file_id}`}>
                    <Eye size={14} className="mr-1" />
                    {stlLoading === f.file_id ? 'Ouverture...' : 'Visualiser'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action history */}
        {data.action_history?.length > 0 && (
          <div className="bg-white border border-zinc-200 p-5 sm:p-7 rounded-sm" data-testid="tracking-history">
            <div className="flex items-center gap-2 mb-4">
              <ClockCounterClockwise size={18} className="text-zinc-700" />
              <p className="label-overline m-0">Historique</p>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {[...data.action_history].reverse().map((h, i) => (
                <div key={`${h.timestamp}-${i}`} className="flex items-start gap-3 text-xs border-b border-zinc-50 pb-2 last:border-0">
                  <span className="text-zinc-400 flex-shrink-0 w-28 font-mono">{formatDateTime(h.timestamp)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-zinc-800">{h.action}</p>
                    {h.details && <p className="text-zinc-500 mt-0.5 break-words">{h.details}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-4 pb-8">
          <p className="text-xs text-zinc-400">Lien de suivi sécurisé — HAMAC SOLUTIONS</p>
        </div>
      </main>
    </div>
  );
}
